import { z } from "zod";

import { MessageIdSchema } from "./branded-id.schemas.js";

/* WHAT WE CAN KNOW ABOUT SOMEONE, one kind per facet.
 *
 * `event_purpose` replaces the retired `event_objective`, which was a compound: it
 * held whatever the attendance claim said, and a claim says several things at once.
 * A production read of 119 member-authored claims found roughly 95 of them naming
 * who they want to meet, so the old kind was mostly `people_to_meet` filed under the
 * wrong name. What is left for `event_purpose` is the residue no other kind claims —
 * "expanding my network", "executive search needs" — and the precedence that keeps
 * it from becoming the dumping ground again is stated in the worker prompt.
 *
 * `event_role` and `background` are the two categories that had no home at all. The
 * same read found ~18 statements with no meeting intent whatsoever: people saying
 * what they are doing AT the event (hosting, filming, answering questions) and
 * people giving their trajectory ("I went from banking technology at … to founding
 * Celcoin"). Both were landing in `event_objective` for want of anywhere better.
 */
export const PARTICIPANT_LEARNING_KINDS = [
  "event_purpose",
  "people_to_meet",
  "people_to_avoid",
  "current_focus",
  "background",
  "event_role",
  "prior_events",
  "known_attendees",
  "current_challenge",
  "can_offer",
  "personal_interests",
] as const;

export const ParticipantLearningKindSchema = z.enum(PARTICIPANT_LEARNING_KINDS);
export type ParticipantLearningKind = z.infer<
  typeof ParticipantLearningKindSchema
>;

export const LEARNING_DISCLOSURES = [
  "open",
  "sensitive",
  "restricted",
] as const;

/**
 * How widely one thing they told us may travel.
 *
 * Ordered least-guarded first, and that order is the semantics: `restricted` is
 * the member's own instruction to hold something back and outranks `sensitive`,
 * which is only ever our own inference about them. A guarded entry is still used
 * to MATCH them — it is what they came for — and is never quoted, paraphrased or
 * alluded to in anything a third party reads.
 */
export const LearningDisclosureSchema = z.enum(LEARNING_DISCLOSURES);
export type LearningDisclosure = z.infer<typeof LearningDisclosureSchema>;

/**
 * One thing they told us, standing on its own.
 *
 * An entry is the unit a later turn addresses: it can be reworded, raised to a
 * stricter level, or dropped without disturbing the entries beside it. That is
 * the whole reason a kind is a list rather than one blob — before this, holding
 * one part of an answer back meant holding all of it back.
 *
 * `reasonForDelta` is present ONLY when the row being written is the one that
 * changed this entry. An entry carried forward untouched has no delta to explain,
 * so it carries no reason, which makes a row self-describing: the entries with a
 * reason are exactly the ones that moved this turn.
 */
export const participantLearningEntrySchema = z.object({
  text: z.string().min(1),
  disclosure: LearningDisclosureSchema,
  reasonForDelta: z.string().min(1).nullish(),
  /* A TOMBSTONE: this entry was taken back, and this row is where it went.
   *
   * It appears on the row that removed it, carrying the reason it went — then it
   * is gone, never carried into the row after. That one row is the only place a
   * removal can explain itself: every other change leaves an entry behind to hold
   * its reason, and a removal leaves none.
   *
   * NOT the record of what was removed — the previous row still holds the entry in
   * full, because rows are append-only. It is the record of WHY.
   */
  removed: z.boolean().nullish(),
});
export type ParticipantLearningEntry = z.infer<
  typeof participantLearningEntrySchema
>;

/** What still stands: the tombstones on this row are not part of the answer. */
export function standingEntries(
  entries: readonly ParticipantLearningEntry[],
): readonly ParticipantLearningEntry[] {
  return entries.filter((entry) => entry.removed !== true);
}

/**
 * The entries a reader cleared to `maxDisclosureLevel` may see.
 *
 * The privacy guarantee, and deliberately its own named function rather than a
 * filter inlined at each read: an audit of "where is disclosure enforced?" has to
 * be a grep, not a reading of every query.
 */
export function entriesAtOrBelow(
  entries: readonly ParticipantLearningEntry[],
  maxDisclosureLevel: LearningDisclosure,
): readonly ParticipantLearningEntry[] {
  const limit = LEARNING_DISCLOSURES.indexOf(maxDisclosureLevel);
  return standingEntries(entries).filter(
    (entry) => LEARNING_DISCLOSURES.indexOf(entry.disclosure) <= limit,
  );
}

/**
 * The kind's entries rendered as one string, for the callers that want prose.
 *
 * Rendering only — it filters nothing. Which entries a reader may see is decided
 * where the read happens, so that a caller cannot widen its own view by choosing
 * how to join.
 *
 * Returns null when there is nothing to render, which the callers distinguish
 * from an empty string.
 */
export function joinLearningEntries(
  entries: readonly ParticipantLearningEntry[],
): string | null {
  const visible = entries
    // Entries are separate facts, so they are sentences rather than clauses; a
    // space would run them together. Any period the text already ends with is
    // dropped first so the separator cannot double.
    .map((entry) => entry.text.replace(/\.+$/, ""))
    .filter((text) => text !== "");
  return visible.length === 0 ? null : visible.join(". ");
}

/**
 * One learning as written. Rows are append-only — a later answer for the same
 * kind is a NEW row, never an edit — so the current answer for a kind is its
 * newest row and the ones behind it are how it got there.
 *
 * `entries` is the whole accumulated answer for the kind, every part of it, not
 * the last thing said. There is deliberately no joined `value`: a reader has to
 * come through `joinLearningEntries` and name the level it is cleared for, so
 * nothing can put a guarded entry in front of a third party by forgetting to.
 *
 * `relatedMessageId` is the newest inbound message of the turn that produced the
 * row. A turn is a batch, so it names the message Porter was reacting to rather
 * than every message it read.
 */
export const newParticipantLearningSchema = z.object({
  kind: ParticipantLearningKindSchema,
  entries: z.array(participantLearningEntrySchema).min(1),
  relatedMessageId: MessageIdSchema,
});
export type NewParticipantLearning = z.infer<
  typeof newParticipantLearningSchema
>;

/**
 * The current answer per kind, resolved from the rows — the entries themselves,
 * not a rendering of them.
 *
 * A kind absent from the map has nothing standing for this reader: either it was
 * never answered, or everything under it sits above the level they are cleared
 * for. Those are deliberately indistinguishable — a reader who could tell them
 * apart would learn that something is being held back.
 *
 * Entries rather than one joined string because a join is a rendering decision,
 * and the callers disagree about it: a prompt wants one per line, the matching
 * engine wants them run together, and three callers only ask whether the kind is
 * answered at all. Joining in the data layer would impose one of those on all of
 * them.
 */
export type CurrentParticipantLearnings = Partial<
  Readonly<Record<ParticipantLearningKind, readonly ParticipantLearningEntry[]>>
>;
